import { useEffect, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { fileUrl } from '../api/hooks'
import type { FindingSeverity, FormInstanceStatus } from '../api/types'

export const gradeColor: Record<string, string> = { A: '#008300', B: '#2a78d6', C: '#eda100', D: '#eb6834', E: '#e34948' }
export const severityColor: Record<FindingSeverity, string> = { Minor: '#eda100', Major: '#e34948', ZeroTolerance: '#4a3aa7' }

export function Badge({ children, color, title }: { children: ReactNode; color?: string; title?: string }) {
  return (
    <span className="badge" title={title} style={color ? { borderColor: color, color, background: color + '1a' } : undefined}>
      {children}
    </span>
  )
}

export function GradeBadge({ grade, percent }: { grade?: string | null; percent?: number | null }) {
  if (!grade) return <span className="muted">–</span>
  return (
    <Badge color={gradeColor[grade] ?? undefined}>
      {percent != null ? `%${percent} ` : ''}{grade}
    </Badge>
  )
}

export function StatusBadge({ status }: { status: FormInstanceStatus }) {
  const { t } = useTranslation()
  const color = status === 'Draft' ? '#eb6834' : status === 'Completed' ? '#2a78d6' : status === 'Approved' ? '#008300' : undefined
  return <Badge color={color}>{t(`status.${status}`)}</Badge>
}

/** "Rev. n": shown once a completed report has been revised (revisionNo > 0). */
export function RevBadge({ revisionNo }: { revisionNo?: number | null }) {
  const { t } = useTranslation()
  if (!revisionNo || revisionNo <= 0) return null
  return <Badge color="#7a4fd6" title={t('revise.badgeTitle', { n: revisionNo })}>{t('revise.badge', { n: revisionNo })}</Badge>
}

export function Tabs({ tabs, active, onChange }: { tabs: { key: string; label: string; badge?: ReactNode }[]; active: string; onChange: (k: string) => void }) {
  return (
    <div className="tabs" role="tablist">
      {tabs.map((tab) => (
        <button key={tab.key} role="tab" aria-selected={active === tab.key} className={active === tab.key ? 'active' : ''} onClick={() => onChange(tab.key)}>
          {tab.label} {tab.badge}
        </button>
      ))}
    </div>
  )
}

export function Modal({ title, onClose, children, width = 560 }: { title: string; onClose: () => void; children: ReactNode; width?: number }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" style={{ maxWidth: width }} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="row between">
          <h3 style={{ margin: 0 }}>{title}</h3>
          <button className="ghost" onClick={onClose} aria-label="close">✕</button>
        </div>
        {children}
      </div>
    </div>
  )
}

/** Resolves a signed link once and renders the image; works for photo thumbnails and signature PNGs. */
export function SecureImage({ fileId, alt, className, style, onClick }: { fileId: string; alt: string; className?: string; style?: React.CSSProperties; onClick?: () => void }) {
  const [src, setSrc] = useState<string>()
  useEffect(() => {
    let alive = true
    void fileUrl(fileId).then((u) => alive && setSrc(u)).catch(() => undefined)
    return () => {
      alive = false
    }
  }, [fileId])
  return src ? <img src={src} alt={alt} className={className} style={style} onClick={onClick} loading="lazy" /> : <div className={`img-placeholder ${className ?? ''}`} style={style} />
}

export function Stat({ label, value, tone, hint }: { label: string; value: ReactNode; tone?: 'good' | 'warn' | 'bad'; hint?: string }) {
  const color = tone === 'good' ? '#008300' : tone === 'warn' ? '#eda100' : tone === 'bad' ? '#e34948' : undefined
  return (
    <div className="stat card">
      <div className="muted small">{label}</div>
      <div className="stat-value" style={{ color }}>{value}</div>
      {hint ? <div className="muted small">{hint}</div> : null}
    </div>
  )
}

export function Empty({ text }: { text: string }) {
  return <p className="muted" style={{ textAlign: 'center', padding: '2rem 0' }}>{text}</p>
}

const pad2 = (n: number) => String(n).padStart(2, '0')

/** Date-only display (dd/MM/yyyy). Pure string slicing so yyyy-MM-dd values never shift by timezone. */
export function fmtDate(d?: string | null): string {
  if (!d) return '–'
  const [y, m, day] = d.slice(0, 10).split('-')
  if (!y || !m || !day) return '–'
  return `${day}/${m}/${y}`
}

/** Timestamp display in LOCAL time (dd/MM/yyyy HH:mm). Use for createdAt/updatedAt-style ISO values. */
/** "01/10/2026 09:05 – 16:40": audit date(s) with the recorded start / end times (times are "HH:mm[:ss]" from the API). */
export function fmtAuditPeriod(start?: string | null, startTime?: string | null, end?: string | null, endTime?: string | null): string {
  if (!start) return ''
  const hm = (t?: string | null) => (t ? t.slice(0, 5) : '')
  const sameDay = !end || end === start
  const from = `${fmtDate(start)}${startTime ? ` ${hm(startTime)}` : ''}`
  if (sameDay) return endTime && startTime && hm(endTime) !== hm(startTime) ? `${from} – ${hm(endTime)}` : from
  return `${from} – ${fmtDate(end)}${endTime ? ` ${hm(endTime)}` : ''}`
}

export function fmtDateTime(iso?: string | null): string {
  if (!iso) return '–'
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) return fmtDate(iso)
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '–'
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`
}

/** Time-of-day in LOCAL time (HH:mm). */
export function fmtTime(iso?: string | null): string {
  if (!iso) return '–'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '–'
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`
}
